import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useToast } from '@/hooks/use-toast'
import { RefreshCw, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

// Single-field schema for the composer's own draft text. Required-ness
// mirrors the previous manual `if (!text.trim())` guard in handleSend.
// Deliberately a non-transforming refine, not `.trim().min(1)` - Zod's
// `.trim()` transforms the parsed value itself, which would silently strip
// intentional leading/trailing whitespace or line breaks from the message
// actually sent to the guest (Codex PR review, same finding as
// suggested-reply-card.tsx/thank-you-message-dialog.tsx); this only checks
// non-blankness, it never rewrites the text. There's no visible label/error
// chrome for this field (it never rendered one), so the message text is
// only ever used internally by the resolver, not surfaced via FormMessage.
const composerSchema = z.object({
  text: z.string().refine(value => value.trim().length > 0, 'Message is required'),
})
type ComposerFormValues = z.infer<typeof composerSchema>

/**
 * Shape of a suggestion object as embedded in thread-message responses -
 * only the fields this component actually reads.
 */
interface Suggestion {
  id?: string | null
  message_id?: string | null
  version?: number | null
  status?: string
  text?: string
  sent_at?: string | null
}

interface SendMeta {
  idempotencyKey: string
  acceptedSuggestion: Suggestion | null
}

interface ComposerProps {
  suggestion?: Suggestion | null
  threadId?: string | null
  onSend?: (text: string, meta: SendMeta) => Promise<void> | void
  onRegenerate?: (suggestion: Suggestion) => void
  disabled?: boolean
  placeholder?: string
  /**
   * The idempotencyKey of a Send that was just retried and delivered via a
   * path OTHER than this composer's own Send button - specifically
   * useThreadOrchestration's inline Resend/Resend-anyway action on a
   * failed/ambiguous bubble in thread-pane.tsx. When this matches the
   * draft this composer is still holding onto after that same send
   * failed/came back ambiguous, the draft is stale (already delivered)
   * and is cleared exactly as a successful Send from this composer would.
   */
  externallyResentIdempotencyKey?: string | null
}

/**
 * Message composer for the new inbox view.
 *
 * Replaces `SuggestedReplyCard`'s role in this view only (the original
 * component stays as-is for its existing callers - see
 * src/components/conversation/suggested-reply-card.jsx). Per the agreed
 * mock (docs/archive/messages-redesign-plan.md, "Suggested reply"), a ready AI
 * suggestion is no longer a separate card: it renders as dimmed ghost text
 * sitting inside the composer's own textarea, like an inline autocomplete
 * suggestion (the same pattern Claude Code's own composer uses). Pressing
 * Tab while the box is empty promotes the ghost text to real, editable
 * text - it never sends anything by itself.
 *
 * Sending is always an explicit user action (root CLAUDE.md: "Reply
 * suggestions: operators always review before sending - system never
 * auto-sends"). Tab only fills the textarea; only clicking Send calls
 * `onSend(text, { idempotencyKey, acceptedSuggestion })`. The idempotency
 * key is generated once per logical send attempt and reused across retries
 * of the exact same text (mirroring suggested-reply-card.jsx's own
 * reuse-on-retry logic) - callers wiring this into
 * `api.sendSuggestionMessage` (which requires the same key on an
 * ambiguous-failure retry) should pass it straight through rather than
 * generating their own. `acceptedSuggestion` is the exact suggestion object
 * that was Tab-accepted for this text (pinned at accept time, `null` if the
 * text wasn't derived from accepting a suggestion) - callers should send
 * against `acceptedSuggestion.version`, not the (possibly since-regenerated)
 * `suggestion` prop, so a version reviewed via Tab is never confused with a
 * newer, unreviewed one that arrived while Send was still pending.
 *
 * Presentational: consumes the same suggestion shape already used by
 * SuggestedReplyCard (`status`/`text`/`version`) - the caller passes the thread's latest
 * suggestion (a single object, already picked from that array), not new
 * data-fetching logic here.
 *
 * Pass `threadId` when the same mounted composer instance may be reused
 * across different conversations (e.g. selecting a different row in the
 * conversation list without navigating/remounting) - the in-progress draft
 * and just-sent lock are reset whenever it changes, so a draft typed for
 * one thread can never be sent into a different one (Codex PR review).
 */
export function Composer({
  suggestion = null,
  threadId = null,
  onSend,
  onRegenerate,
  disabled = false,
  placeholder = 'Type a message...',
  externallyResentIdempotencyKey = null,
}: ComposerProps) {
  const { toast } = useToast()
  const form = useForm<ComposerFormValues>({
    resolver: zodResolver(composerSchema),
    defaultValues: { text: '' },
  })
  // Subscribes this component to re-render on every keystroke, exactly like
  // the plain `value` useState it replaces - every other piece of local UI
  // logic below (showGhost, canSend, the ghost overlay text) reads this the
  // same way it used to read `value`.
  const value = useWatch({ control: form.control, name: 'text' })
  const { ref: textFieldRef, onChange: textFieldOnChange, ...textFieldRest } = form.register('text')
  const [sending, setSending] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const previousThreadIdRef = useRef(threadId)
  // Bumped on every thread switch AND on every new send attempt - the
  // authority handleSend's async continuation checks after `await
  // onSend(...)` resolves, instead of comparing threadId directly (Codex
  // PR review: a bare threadId comparison can't tell a truly stale
  // completion apart from one that happens to match again, e.g. switching
  // A -> B -> back to A before A's first send resolves, or a second send
  // started on the SAME thread while an earlier one is still pending).
  // Only the completion whose captured generation still equals the current
  // one is allowed to mutate `value`/`justSentKey`/`sending`.
  const sendGenerationRef = useRef(0)
  // Identifies the specific suggestion (by version, falling back to its
  // text) that was just successfully sent - kept locally until the caller's
  // refetch replaces `suggestion` with authoritative backend state (Codex PR
  // review: without this, a successful send clears `value`, which makes the
  // still-unchanged `completed` suggestion in props show as ghost text
  // again, letting the operator accept-and-send the exact same reply twice
  // during refetch latency). Mirrors the same lock in
  // src/components/conversation/suggested-reply-card.jsx (justSentVersion).
  const [justSentKey, setJustSentKey] = useState<string | null>(null)
  // Idempotency key for the CURRENT logical send attempt, reused across
  // retries of the exact same draft text (Codex PR review: the repo's only
  // reply-send API, api.sendSuggestionMessage, requires reusing the same
  // idempotencyKey when retrying an ambiguous failure - see
  // handleOpenConfirmSend in suggested-reply-card.jsx - otherwise a retry
  // of a send that actually reached the backend can deliver the guest
  // message twice). Only regenerated when the text actually changes to a
  // genuinely different logical send, exactly mirroring that same pattern.
  const pendingIdempotencyKeyRef = useRef<string | null>(null)
  const pendingIdempotencyTextRef = useRef<string | null>(null)
  // The suggestion object pinned at the moment Tab accepted it (Codex PR
  // review): if a regeneration already in flight publishes a newer version
  // after the operator accepts this one but before they click Send, `onSend`
  // must still identify the version actually reviewed and accepted - not
  // whatever `suggestion` happens to be by the time the request goes out.
  // Mirrors editingVersion in suggested-reply-card.jsx (pinned at edit-start).
  const [acceptedSuggestion, setAcceptedSuggestion] = useState<Suggestion | null>(null)

  // Reset the draft and just-sent lock when the caller reports a different
  // thread - without this, a mounted-but-reused composer would carry the
  // previous thread's unsent draft forward and send it into the wrong
  // conversation (Codex PR review).
  useEffect(() => {
    if (previousThreadIdRef.current !== threadId) {
      previousThreadIdRef.current = threadId
      // Invalidates any send still in flight for whatever was selected
      // before this switch - its own completion is guarded by
      // sendGenerationRef to skip mutating this new thread's state.
      sendGenerationRef.current += 1
      form.reset({ text: '' })
      setJustSentKey(null)
      setSending(false)
      pendingIdempotencyKeyRef.current = null
      pendingIdempotencyTextRef.current = null
      setAcceptedSuggestion(null)
    }
  }, [threadId, form])

  // Clears a draft this composer is only still holding because ITS OWN
  // Send attempt failed/came back ambiguous, once that exact attempt is
  // confirmed delivered via a DIFFERENT path - useThreadOrchestration's
  // inline Resend/Resend-anyway action on the bubble in thread-pane.tsx
  // (Codex PR review). Matched by idempotencyKey, not text: text alone
  // could coincidentally match a brand-new, unrelated draft the operator
  // has since started typing for a different message. Only clears when it
  // still matches this composer's own pending key - a resend for some
  // OTHER, already-abandoned attempt must never reach back and wipe
  // whatever the operator is doing now.
  useEffect(() => {
    if (!externallyResentIdempotencyKey) return
    if (pendingIdempotencyKeyRef.current !== externallyResentIdempotencyKey) return
    if (form.getValues('text') === pendingIdempotencyTextRef.current) {
      form.setValue('text', '')
    }
    pendingIdempotencyKeyRef.current = null
    pendingIdempotencyTextRef.current = null
    setAcceptedSuggestion(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externallyResentIdempotencyKey])

  // Prefer `id` (already globally unique per the real suggestion shape -
  // see completedSuggestion() in suggested-reply-card.test.jsx). Otherwise
  // combine `message_id` with `version` - version alone commonly restarts
  // at 1 per thread/message and would otherwise let an unrelated version-1
  // suggestion from a different thread satisfy `wasSent` just because the
  // mounted composer switched threads without remounting. Text is the last
  // resort only when neither identifier is present.
  const suggestionKey = suggestion
    ? (suggestion.id ??
      (suggestion.message_id && suggestion.version != null
        ? `${suggestion.message_id}:${suggestion.version}`
        : null) ??
      suggestion.text ??
      null)
    : null

  const status = useMemo(() => {
    if (!suggestion) return 'not-generated'
    const statusLower = (suggestion.status || '').toLowerCase()
    if (statusLower === 'queued') return 'generating'
    return statusLower || 'not-generated'
  }, [suggestion])

  const isGenerating = status === 'generating'
  const hasSuggestionText = Boolean(suggestion?.text && suggestion.text.trim().length > 0)
  // The local justSentKey lock only bridges the gap until a refetch lands.
  // Once it does, `sent_at` is the backend's own authoritative record that
  // this suggestion was already sent (same signal SuggestedReplyCard's
  // `isSent` relies on) - checked independently so an already-sent
  // suggestion can never show as ghost text again, even after the local
  // lock is cleared by a remount or replaced by a newer suggestion.
  const wasSent =
    Boolean(suggestion?.sent_at) || (suggestionKey !== null && suggestionKey === justSentKey)
  const isSuggestionReady = status === 'completed' && hasSuggestionText && !wasSent

  // Ghost text only makes sense while the operator hasn't typed anything -
  // once there's real content, typing takes over and the ghost never
  // interferes with it again for this suggestion.
  const showGhost = isSuggestionReady && value.length === 0

  const acceptGhost = () => {
    if (!showGhost || !suggestion?.text) return
    const text = suggestion.text
    // Pin exactly which suggestion was accepted, before any later
    // regeneration can move `suggestion` on from under this draft.
    setAcceptedSuggestion(suggestion)
    form.setValue('text', text, { shouldValidate: true, shouldDirty: true })
    // Cursor at the end of the promoted text, matching how autocomplete
    // acceptance normally behaves.
    requestAnimationFrame(() => {
      const el = textareaRef.current
      if (el) {
        el.focus()
        el.setSelectionRange(text.length, text.length)
      }
    })
  }

  // Sends the current draft. Explicit-only (Tab never reaches this path -
  // it only promotes ghost text into the textarea, see handleKeyDown). The
  // draft is preserved on failure so a rejected send is retryable instead
  // of silently losing the operator's message - only a confirmed success
  // clears the input (root CLAUDE.md: backend responses define truth, no
  // optimistic clearing of unconfirmed state).
  //
  // Called via `form.handleSubmit(onSubmit)` - the resolver's refine
  // already stands in for the old `if (!text.trim())` guard (onSubmit is
  // simply never invoked for an empty/whitespace-only draft), but
  // `data.text` is NOT trimmed - it's exactly what the operator typed.
  const onSubmit = async (data: ComposerFormValues) => {
    const text = data.text
    if (sending) return
    // A missing onSend is a wiring bug, not a successful send - reported
    // as a failure (draft preserved) rather than silently clearing the
    // draft and locking the suggestion as if a real mutation had happened
    // (Codex PR review).
    if (typeof onSend !== 'function') {
      toast({
        title: 'Failed to send message',
        description: 'Sending is not available right now.',
        variant: 'destructive',
      })
      return
    }
    const sentForSuggestionKey = suggestionKey
    // This send's own generation - see sendGenerationRef for why a bare
    // threadId comparison isn't enough (A -> B -> back to A, or a second
    // send on the same thread while an earlier one is still pending).
    sendGenerationRef.current += 1
    const sentGeneration = sendGenerationRef.current
    // Reuse the same idempotency key across retries of this exact text -
    // only a genuinely different draft gets a fresh one.
    if (pendingIdempotencyTextRef.current !== text) {
      pendingIdempotencyKeyRef.current = crypto.randomUUID()
      pendingIdempotencyTextRef.current = text
    }
    const idempotencyKey = pendingIdempotencyKeyRef.current as string
    // The suggestion actually reviewed and accepted for this exact text,
    // pinned at acceptGhost time - NOT `suggestion`, which may have already
    // moved on to a newer, unreviewed version by the time Send is clicked.
    // Exact-string comparison (not `.trim()` on either side) - `acceptGhost`
    // sets the field to the suggestion's raw, untrimmed text, and `text` is
    // now also untrimmed (Codex PR review), so an unedited accepted
    // suggestion is an exact match; trimming only one side would lose
    // attribution for a suggestion with leading/trailing whitespace.
    const sentForAcceptedSuggestion =
      acceptedSuggestion && text === acceptedSuggestion.text ? acceptedSuggestion : null
    setSending(true)
    try {
      await onSend(text, { idempotencyKey, acceptedSuggestion: sentForAcceptedSuggestion })
      // A newer thread switch or a newer send attempt has already moved
      // this composer on - this now-stale completion must not reach back
      // and clear or lock that newer state (Codex PR review).
      if (sendGenerationRef.current !== sentGeneration) return
      // Only clear the draft if it still matches what was actually
      // submitted - the textarea stays editable during the request, so the
      // operator may have kept typing past the captured text before this
      // resolved. Clearing unconditionally would silently discard those
      // newer edits even though the backend only confirmed the earlier text.
      // Exact-string comparison (not `.trim()`) - `text` is the untrimmed
      // value actually sent (Codex PR review: the schema no longer trims),
      // so trimming just one side here would make this always-false for a
      // draft with leading/trailing whitespace, leaving a sent draft in the
      // box and inviting an accidental duplicate send.
      if (form.getValues('text') === text) {
        form.setValue('text', '')
      }
      // This logical send is done - a future send of the identical text is
      // a brand-new attempt, not a retry, and must get its own fresh key.
      pendingIdempotencyKeyRef.current = null
      pendingIdempotencyTextRef.current = null
      setAcceptedSuggestion(null)
      // Lock the CURRENT suggestion from reappearing as ghost text after
      // ANY successful send, not only when the sent text matches it
      // exactly (Codex PR review): once the operator has sent something -
      // whether that's the suggestion verbatim or a manually authored
      // reply that ignores it - the conversation has moved on, so this
      // suggestion is stale until the caller's refetch brings a genuinely
      // new one (or none at all).
      if (sentForSuggestionKey !== null) {
        setJustSentKey(sentForSuggestionKey)
      }
    } catch (error) {
      if (sendGenerationRef.current !== sentGeneration) return
      toast({
        title: 'Failed to send message',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      })
    } finally {
      if (sendGenerationRef.current === sentGeneration) {
        setSending(false)
      }
    }
  }

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = e => {
    // !e.shiftKey (Codex PR review): Shift+Tab is backward focus
    // navigation, not acceptance - without this guard, a keyboard user
    // trying to move to the previous control instead accepts the
    // suggestion and gets refocused into this textarea by acceptGhost.
    if (e.key === 'Tab' && !e.shiftKey && showGhost) {
      e.preventDefault()
      acceptGhost()
      return
    }
    // Cmd/Ctrl+Enter sends from the textarea itself - still an explicit
    // user action, never automatic. Native form submission isn't in play
    // here (there's no <form> element - plain Enter must still insert a
    // newline like any other textarea), so the resolver-validated submit is
    // triggered imperatively instead of relying on a submit event.
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      void form.handleSubmit(onSubmit)()
    }
  }

  const canSend = value.trim().length > 0 && !disabled && !sending
  // `form.handleSubmit(onSubmit)` reads internal refs, so it must be
  // invoked from an event handler, not called inline during render (the
  // Send button used to do that, which the React Compiler correctly flags
  // as a ref access during render).
  const handleSendClick = () => {
    void form.handleSubmit(onSubmit)()
  }

  return (
    <div className="border-t border-border p-3 space-y-2" data-testid="composer">
      {isGenerating && (
        <div
          className="flex items-center gap-1.5 text-xs text-textMuted"
          data-testid="composer-generating"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>Thinking…</span>
        </div>
      )}
      {showGhost && (
        <div
          className="flex items-center justify-between gap-2 text-xs text-textMuted"
          data-testid="composer-suggestion-hint"
        >
          <span className="flex items-center gap-1.5">
            <span
              className="inline-flex h-4 min-w-4 items-center justify-center rounded border border-borderStrong bg-surface2 px-1 font-mono text-[10px] leading-none"
              aria-hidden="true"
            >
              Tab
            </span>
            to use suggestion
          </span>
          {onRegenerate && (
            <button
              type="button"
              onClick={() => onRegenerate(suggestion as Suggestion)}
              // sending (Codex PR review): the textarea stays editable
              // during a request, so an operator can delete an in-flight
              // draft back to empty, making showGhost (and this button)
              // true again while a send is still pending - clicking
              // Regenerate then could publish a newer suggestion mid-send,
              // recreating the same stale-ghost/double-response race the
              // standalone Regenerate button below already guards against.
              disabled={disabled || sending}
              className="flex items-center gap-1 text-textMuted hover:text-text transition-colors disabled:opacity-50 disabled:pointer-events-none"
            >
              <RefreshCw className="h-3 w-3" />
              Regenerate
            </button>
          )}
        </div>
      )}
      <div className="relative">
        {showGhost && (
          <div
            aria-hidden="true"
            data-testid="composer-ghost-text"
            // overflow-hidden mirrors the textarea's own bounds - a
            // suggestion longer than the visible rows must clip/scroll like
            // real textarea content, not spill over and obscure the
            // Regenerate/Send controls below it.
            className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap wrap-break-word px-3 py-2 text-sm leading-relaxed text-white/60"
          >
            {suggestion?.text}
          </div>
        )}
        <textarea
          {...textFieldRest}
          ref={el => {
            textFieldRef(el)
            textareaRef.current = el
          }}
          onChange={e => {
            textFieldOnChange(e)
            // Clearing the draft back to empty means the operator is no
            // longer working from the accepted suggestion - a fresh Tab
            // accept (of whatever suggestion is current then) is a new pin.
            if (e.target.value.length === 0) setAcceptedSuggestion(null)
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={3}
          aria-label="Message"
          // Suppress the native placeholder whenever ghost text is showing
          // - this exact double-rendering bug (native placeholder rendering
          // behind the ghost text) was caught and fixed in the original
          // mock. Only ever the plain placeholder, never the suggestion
          // text itself.
          placeholder={showGhost ? '' : placeholder}
          className="relative block w-full resize-none rounded-md border border-borderStrong bg-transparent px-3 py-2 text-sm leading-relaxed text-text placeholder:text-textMuted focus:outline-hidden focus:ring-2 focus:ring-mint disabled:opacity-50"
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        {!showGhost && onRegenerate && suggestion && status !== 'not-generated' && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRegenerate(suggestion)}
            // Also disabled while a draft exists or a send is pending
            // (Codex PR review): regenerating mid-draft can publish a
            // newer suggestion while the operator's own reply is being
            // sent - the send's success only locks the suggestion key it
            // captured, so that newly generated, now-stale reply would
            // otherwise reappear as an acceptable ghost suggestion for the
            // same guest message right after. Mirrors the same guard in
            // SuggestedReplyCard (disabled while isEditing).
            disabled={isGenerating || disabled || sending || value.trim().length > 0}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Regenerate
          </Button>
        )}
        <Button type="button" size="sm" onClick={handleSendClick} disabled={!canSend}>
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </div>
  )
}
